import { EmptySlotResponse } from '../../../library/models/appointment';

/**
 * What a visitor has chosen so far, kept for the length of a tab.
 *
 * It lets the choice survive the three detours the flow takes: a reload, signing in to an
 * existing account half-way through, and coming back from the confirmation link when the time
 * has been taken in the meantime -- which lands on the slot step with the service and therapist
 * still chosen. **Never their email, name, phone or anything they typed into the details step**:
 * sessionStorage is readable by any script on the page, and the one piece of free text kept
 * here, the note, is the booking's own.
 *
 * Keyed by hostname because one browser can hold two clinics' tabs, and a choice of therapist
 * at one means nothing at the other. Every access is guarded: storage can throw in a private
 * window, and a draft that cannot be kept is merely a draft that is not kept.
 */
export interface BookingDraft {
    serviceId: number;
    therapistId?: number | null;
    slot?: EmptySlotResponse | null;
    note?: string;
    savedAt: number;
}

/** Long enough to fetch a password from somewhere; short enough to be forgotten by lunch. */
export const DRAFT_LIFETIME_MS: number = 2 * 60 * 60 * 1000;

const keyFor = (hostname: string): string => `carefull:bookingDraft:${hostname}`;

export const readDraft = (hostname: string, now: number = Date.now()): BookingDraft | null => {
    try {
        const raw: string | null = window.sessionStorage.getItem(keyFor(hostname));

        if (!raw) {
            return null;
        }

        const draft: BookingDraft = JSON.parse(raw);

        if (typeof draft?.serviceId !== 'number' || now - draft.savedAt > DRAFT_LIFETIME_MS) {
            window.sessionStorage.removeItem(keyFor(hostname));
            return null;
        }

        return draft;
    } catch {
        return null;
    }
};

export const saveDraft = (
    hostname: string,
    draft: Omit<BookingDraft, 'savedAt'>,
    now: number = Date.now()
): void => {
    try {
        window.sessionStorage.setItem(keyFor(hostname), JSON.stringify({ ...draft, savedAt: now }));
    } catch {
        // A draft that cannot be kept is not worth an error.
    }
};

export const clearDraft = (hostname: string): void => {
    try {
        window.sessionStorage.removeItem(keyFor(hostname));
    } catch {
        // As above.
    }
};
